import { useState, useRef, useEffect } from "react";
import { Sidebar } from "./Sidebar";
import { ComplianceAssistant, CompanionWorkspace } from "../ComplianceAssistant";
import { useComplianceAssistantStore } from "../ComplianceAssistant/store";
import { useUiContextStore } from "../../../stores/uiContextStore";
import { ProcessingJobHost } from "../ProcessingJobHost";
import { ProcessingStatusBubble } from "../ProcessingStatusBubble";
import { Outlet, useLocation } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import {
  X,
  Menu,
  Bell,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

/* ── Notification data ──────────────────────────────────── */
interface Notification {
  id: string;
  type: "warning" | "success" | "info";
  title: string;
  sub: string;
  read: boolean;
}

const INITIAL_NOTIFICATIONS: Notification[] = [
  {
    id: "n1",
    type: "warning",
    title: "Drift detected on CM-6.1",
    sub: "2 hrs ago",
    read: false,
  },
  {
    id: "n2",
    type: "success",
    title: "POAM-047 approved by Sarah Kim",
    sub: "18 min ago",
    read: false,
  },
  {
    id: "n3",
    type: "info",
    title: "Assessment 2026-02 completed",
    sub: "3 hrs ago",
    read: true,
  },
  {
    id: "n4",
    type: "warning",
    title: "F-215 is 8 days overdue",
    sub: "Today",
    read: false,
  },
];

/* ── MainLayout ─────────────────────────────────────────── */
export function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const companionOpen = useComplianceAssistantStore((s) => s.open);
  const companionMinimized = useComplianceAssistantStore((s) => s.minimized);
  const showCompanionWorkspace = companionOpen && !companionMinimized;

  /* ── Warm the companion's suggestions ─────────────────────
     Here rather than in ChatPanel because ChatPanel is mounted only while the panel is open, so its
     own effect cannot start the request until after the click — and the request is a model call, which
     is the whole of the wait the user feels. This layout is a pathless route that survives navigation,
     so it can fetch for the page while the panel is still shut.

     Gated on the panel having been opened once this session: generation is not free, and a user who
     never touches the assistant should never pay for one. After the first open every page is warm by
     the time they click. Keyed on the record and the view as well as the path, because the OSCAL
     authoring pages leave the URL on the list route while the content behind it changes. `loadSuggestions`
     itself drops a repeat of an unchanged context and skips entirely once a conversation is under way,
     so nothing here fires twice. */
  const { pathname, search } = useLocation();
  const hasEverOpened = useComplianceAssistantStore((s) => s.hasEverOpened);
  const loadSuggestions = useComplianceAssistantStore((s) => s.loadSuggestions);
  const companionView = useUiContextStore((s) => s.view);
  const companionItemId = useUiContextStore((s) => s.selectedItem?.id ?? null);
  useEffect(() => {
    if (!hasEverOpened) return;
    void loadSuggestions(pathname);
  }, [hasEverOpened, pathname, companionView, companionItemId, loadSuggestions]);

  // MainLayout only renders once a child route has actually matched, so every
  // pathname seen here is a valid page. Remembering it lets an unmatched/typo'd
  // deep link (e.g. /cmdb) bounce the user back to where they were instead of a
  // fixed default — see the catch-all route in app/routes.ts.
  useEffect(() => {
    sessionStorage.setItem("lastVisitedPath", pathname + search);
  }, [pathname, search]);

  /* notification state */
  const [notifications, setNotifications] = useState<Notification[]>(
      INITIAL_NOTIFICATIONS,
  );
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifPos, setNotifPos] = useState({ top: 0, right: 0 });
  const bellRef = useRef<HTMLButtonElement>(null);
  const unread = notifications.filter((n) => !n.read).length;

  // Reads as unused because the bell <button> that calls it is commented out below.
  // Kept so the notification UI can be restored by uncommenting that button.
  // noinspection JSUnusedLocalSymbols
  const handleBellClick = () => {
    if (bellRef.current) {
      const r = bellRef.current.getBoundingClientRect();
      setNotifPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    }
    setShowNotifs((p) => !p);
  };

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
      <div className="flex h-screen bg-[#f5f7fb] dark:bg-[#09090f] overflow-hidden transition-colors duration-200" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        {/* Mobile overlay */}
        {mobileSidebarOpen && (
            <div
                className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
                onClick={() => setMobileSidebarOpen(false)}
            />
        )}

        {/* Sidebar */}
        <Sidebar
            collapsed={sidebarCollapsed}
            onExpand={() => setSidebarCollapsed(false)}
            onCollapse={() => setSidebarCollapsed(true)}
            mobileOpen={mobileSidebarOpen}
            onMobileClose={() => setMobileSidebarOpen(false)}
        />

        {/* Main area */}
        <div className="flex flex-co  l flex-1 min-w-0 overflow-hidden">
          {/* Mobile-only top bar */}
          <div
              className="lg:hidden flex-shrink-0 flex items-center gap-3 px-4 h-[52px]
          bg-white dark:bg-[#07070e] border-b border-[#e2e8f0] dark:border-white/[0.05]"
          >
            <button
                onClick={() => setMobileSidebarOpen(true)}
                className="p-1.5 rounded-lg text-[#94a3b8] dark:text-white/30
              hover:text-[#475569] dark:hover:text-white/70 hover:bg-[#f1f5f9]
              dark:hover:bg-white/5 transition-colors flex-shrink-0"
            >
              <Menu className="size-4" />
            </button>

            <div className="flex items-center gap-2 flex-1">
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <defs>
                  <linearGradient id="mob-shield-1" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#A18DC2" />
                    <stop offset="50%" stopColor="#BAC3E3" />
                    <stop offset="100%" stopColor="#A18DC2" />
                  </linearGradient>
                  <linearGradient id="mob-shield-2" x1="100%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#CE7EB3" />
                    <stop offset="50%" stopColor="#D6A0C8" />
                    <stop offset="100%" stopColor="#A090C4" />
                  </linearGradient>
                </defs>
                <path d="M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3" fill="url(#mob-shield-1)" />
                <path d="M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3" fill="url(#mob-shield-2)" />
                <path d="M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z" fill="white" />
              </svg>
              <span className="text-sm font-black text-[#0f172a] dark:text-white tracking-tight">
              RegO
            </span>
            </div>
          </div>

          <main className="flex-1 overflow-hidden bg-[#f5f7fb] dark:bg-[#09090f] flex">
            <div
                data-app-scroll
                className={`h-full min-w-0 flex-1 overflow-y-auto p-4 sm:p-5 lg:p-7 flex-col ${
                    showCompanionWorkspace ? "hidden lg:flex" : "flex"
                }`}
            >
              <Outlet />
            </div>
            {showCompanionWorkspace && (
                <div className="h-full w-full flex-shrink-0 overflow-hidden lg:w-[480px] lg:border-l lg:border-[#e2e8f0] dark:lg:border-white/[0.06]">
                  <CompanionWorkspace />
                </div>
            )}
          </main>
        </div>

        {/* Background validation run - the console outlives the page that
            started it, and the bubble shows its status on every page. */}
        <ProcessingJobHost />
        <ProcessingStatusBubble />

        {/* ══════════════════════════════════════════════════
          FIXED TOP-RIGHT - Notification Bell
          ══════════════════════════════════════════════════ */}
        {/* <button
        ref={bellRef}
        onClick={handleBellClick}
        title={`Notifications${unread > 0 ? ` (${unread})` : ""}`}
        className={`fixed top-4 right-4 bottom-4 z-40 flex items-center justify-center size-10 rounded-xl
          shadow-md transition-all duration-150
          ${
            showNotifs
              ? "bg-indigo-600 dark:bg-indigo-600 text-white shadow-indigo-500/30 dark:shadow-indigo-500/30"
              : "bg-white dark:bg-[#131320] text-[#64748b] dark:text-white/50 border border-[#e2e8f0] dark:border-white/[0.08] hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-200 dark:hover:border-indigo-500/30 hover:shadow-lg"
          }`}
      >
        <div className="relative">
          <Bell className="size-[18px]" />
          {unread > 0 && (
            <span
              className={`absolute -top-1.5 -right-1.5 size-[15px] rounded-full text-[8px] font-black flex items-center justify-center
              ${showNotifs ? "bg-white text-indigo-600" : "bg-indigo-600 dark:bg-indigo-500 text-white"}`}
            >
              {unread}
            </span>
          )}
        </div>
      </button> */}

        {/* Notification Panel */}
        <AnimatePresence>
          {showNotifs && (
              <>
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowNotifs(false)}
                />
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -8 }}
                    transition={{ type: "spring", stiffness: 420, damping: 28 }}
                    style={{
                      position: "fixed",
                      top: notifPos.top,
                      right: notifPos.right,
                      zIndex: 50,
                    }}
                    className="w-80 bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] shadow-2xl shadow-black/10 dark:shadow-black/50 overflow-hidden"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#f1f5f9] dark:border-white/[0.05]">
                    <div className="flex items-center gap-2">
                      <Bell className="size-3.5 text-indigo-600 dark:text-indigo-400" />
                      <h4 className="text-xs font-black text-[#0f172a] dark:text-white tracking-wide">
                        Notifications
                      </h4>
                      {unread > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-indigo-600 dark:bg-indigo-500 text-white text-[9px] font-black">
                      {unread} new
                    </span>
                      )}
                    </div>
                    <button
                        onClick={() => setShowNotifs(false)}
                        className="p-1 rounded text-[#94a3b8] dark:text-white/30 hover:bg-[#f1f5f9] dark:hover:bg-white/5 transition-colors"
                    >
                      <X className="size-3" />
                    </button>
                  </div>

                  {/* Items */}
                  <div className="divide-y divide-[#f1f5f9] dark:divide-white/[0.04] max-h-72 overflow-y-auto">
                    {notifications.map((n) => (
                        <div
                            key={n.id}
                            className={`flex items-start gap-3 px-4 py-3 hover:bg-[#f8f7fb] dark:hover:bg-white/[0.03] cursor-pointer transition-colors
                      ${!n.read ? "bg-indigo-50/40 dark:bg-indigo-500/[0.04]" : ""}`}
                        >
                          <div
                              className={`size-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  n.type === "warning"
                                      ? "bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400"
                                      : n.type === "success"
                                          ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                                          : "bg-sky-100 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400"
                              }`}
                          >
                            {n.type === "warning" ? (
                                <AlertTriangle className="size-3" />
                            ) : (
                                <CheckCircle className="size-3" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                                className={`text-xs truncate ${!n.read ? "font-bold text-[#0f172a] dark:text-white" : "font-medium text-[#475569] dark:text-white/50"}`}
                            >
                              {n.title}
                            </p>
                            <p className="text-[10px] text-[#94a3b8] dark:text-white/25 mt-0.5">
                              {n.sub}
                            </p>
                          </div>
                          {!n.read && (
                              <span className="size-1.5 rounded-full bg-indigo-500 dark:bg-indigo-400 flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-2.5 border-t border-[#f1f5f9] dark:border-white/[0.05]">
                    <button
                        onClick={markAllRead}
                        className="w-full text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors tracking-wide"
                    >
                      Mark all as read
                    </button>
                  </div>
                </motion.div>
              </>
          )}
        </AnimatePresence>

        {/* Floating Compliance Assistant - authenticated pages only (not /login) */}
        <ComplianceAssistant />
      </div>
  );
}
